import i18n from '@/i18n';
/**
 * МАКЕТ «Торговий графік» — демонстраційні дані.
 *
 * Жодного звернення до API: це каркас для обговорення з Вадимом, Олею та
 * технічним диспетчером (концепція від 13.10, розділи 8–11). Числа правдоподібні
 * для СЕС «Еліос Лучки» у середині жовтня, але вигадані — тому на екрані стоїть
 * позначка «макет», щоб ніхто не прийняв їх за робочі.
 */

export interface HourRow {
  /** Година доби, 1 = 00:00–01:00. */
  h: number;
  /** Прогноз генерації, МВт·год: нижня межа, медіана, верхня. */
  genP10: number;
  genP50: number;
  genP90: number;
  /** Заряд УЗЕ з генерації, МВт·год (у графік відпуску не входить). */
  charge: number;
  /** Розряд УЗЕ в мережу, МВт·год. */
  discharge: number;
  /** Графік відпуску = генерація − заряд + розряд. */
  plan: number;
  /** Прогноз ціни РДН, грн/МВт·год: медіана й коридор. */
  priceP50: number;
  priceP10: number;
  priceP90: number;
  /** Чому саме так — пам’ятка для того, хто ставить галочку. */
  why: string;
}

const RAW: Array<[number, number, number, number, number, number, string]> = [
  // h, genP50, charge, discharge, priceP50, priceSpread, why
  [1, 0, 0, 0, 5200, 0.22, 'ak_sd_night_no_generation_the_night_draw_of'],
  [2, 0, 0, 0, 4900, 0.22, 'ak_sd_night_no_generation'],
  [3, 0, 0, 0, 4700, 0.24, 'ak_sd_night_no_generation'],
  [4, 0, 0, 0, 4600, 0.24, 'ak_sd_night_no_generation'],
  [5, 0, 0, 0, 4800, 0.26, 'ak_sd_night_no_generation'],
  [6, 0, 0, 0, 5600, 0.28, 'ak_sd_night_no_generation'],
  [7, 0, 0, 0, 7400, 0.30, 'ak_sd_before_sunrise'],
  [8, 0.6, 0, 0, 9100, 0.34, 'ak_sd_sunrise_the_model_gives_0_6_with_a_0_4'],
  [9, 3.2, 0, 0, 8200, 0.30, 'ak_sd_morning_ramp_historically_the_largest'],
  [10, 6.8, 0, 0, 5400, 0.28, 'ak_sd_ramp_continues_variable_cloud_cover'],
  [11, 9.4, 0, 0, 1900, 0.40, 'ak_sd_price_is_already_low_but_the_battery_i'],
  [12, 11.0, 11.0, 0, 620, 0.55, 'ak_sd_zero_hour_all_generation_goes_to_the_b'],
  [13, 11.4, 11.4, 0, 480, 0.60, 'ak_sd_zero_hour_the_battery_takes_the_remain'],
  [14, 10.6, 8.6, 0, 520, 0.58, 'ak_sd_battery_is_full_the_remaining_2_0_goes'],
  [15, 8.8, 0, 0, 760, 0.50, 'ak_sd_delivery_per_schedule'],
  [16, 6.2, 0, 0, 1800, 0.42, 'ak_sd_delivery_per_schedule'],
  [17, 3.4, 0, 0, 3600, 0.34, 'ak_sd_generation_decline'],
  [18, 1.2, 0, 0, 6900, 0.30, 'ak_sd_sunset'],
  [19, 0.2, 0, 0, 10400, 0.26, 'ak_sd_almost_no_generation_price_is_rising'],
  [20, 0, 0, 13.5, 12600, 0.22, 'ak_sd_discharge_window_2nd_most_expensive_ho'],
  [21, 0, 0, 13.5, 13200, 0.20, 'ak_sd_discharge_window_the_most_expensive_ho'],
  [22, 0, 0, 0, 11800, 0.22, 'ak_sd_battery_is_empty_no_reserve_for_a_thir'],
  [23, 0, 0, 0, 9200, 0.24, 'ak_sd_night'],
  [24, 0, 0, 0, 7100, 0.24, 'ak_sd_night'],
];

export const HOURS: HourRow[] = RAW.map(([h, gen, charge, discharge, price, spread, why]) => ({
  h,
  genP50: gen,
  genP10: Math.round(gen * 0.72 * 10) / 10,
  genP90: Math.round(gen * 1.26 * 10) / 10,
  charge,
  discharge,
  plan: Math.round((gen - charge + discharge) * 10) / 10,
  priceP50: price,
  priceP10: Math.round(price * (1 - spread)),
  priceP90: Math.round(price * (1 + spread)),
  get why() { return i18n.t(why); },
}));

/** Ранг години за ціною: 1 = найдорожча. */
export const PRICE_RANK: Record<number, number> = (() => {
  const sorted = [...HOURS].sort((a, b) => b.priceP50 - a.priceP50);
  const map: Record<number, number> = {};
  sorted.forEach((row, i) => { map[row.h] = i + 1; });
  return map;
})();

export const DISCHARGE_WINDOW = [20, 21];
export const CHARGE_WINDOW = [12, 13, 14];

export const TOTALS = {
  genMwh: Math.round(HOURS.reduce((a, r) => a + r.genP50, 0) * 10) / 10,
  planMwh: Math.round(HOURS.reduce((a, r) => a + r.plan, 0) * 10) / 10,
  chargeMwh: Math.round(HOURS.reduce((a, r) => a + r.charge, 0) * 10) / 10,
  dischargeMwh: Math.round(HOURS.reduce((a, r) => a + r.discharge, 0) * 10) / 10,
  /** Очікувана виручка = графік × прогноз ціни × 0,97 (знижка СВБ). */
  revenueUah: Math.round(HOURS.reduce((a, r) => a + r.plan * r.priceP50 * 0.97, 0)),
};

/** Технічний стан станції на добу подачі. */
export const TECH_STATE = {
  invertersTotal: 64,
  invertersAlive: 62,
  /** Коефіцієнт доступності — накладається РІВНО ОДИН РАЗ і лише за подією в журналі. */
  availability: 0.97,
  bessUsefulMwh: 27,
  bessChargeMw: 15,
  bessDischargeMw: 13.5,
  bessRoundTrip: 0.87,
  socPct: 18,
  gridLimitMw: 16,
  osrLimit: null as string | null,
};

export interface TechEvent {
  at: string;
  kind: 'ремонт' | 'звʼязок' | 'обмеження';
  text: string;
  affectsSchedule: boolean;
}

export const TECH_EVENTS: TechEvent[] = [
  { at: '11.10 09:14', kind: 'ремонт', get text() { return i18n.t('ak_sd_2_inverters_of_tko_3_taken_out_of_serv'); }, affectsSchedule: true },
  { at: '13.10 02:40', kind: 'звʼязок', get text() { return i18n.t('ak_sd_connection_to_ems_box_lost_for_6_min_d'); }, affectsSchedule: false },
];

/** Точність за останні 7 діб — щоб людина бачила, наскільки вірити числу. */
export const ACCURACY_7D = [
  { d: '06.10', nmae: 9.1 }, { d: '07.10', nmae: 14.5 }, { d: '08.10', nmae: 7.8 },
  { d: '09.10', nmae: 11.2 }, { d: '10.10', nmae: 22.4 }, { d: '11.10', nmae: 8.3 },
  { d: '12.10', nmae: 10.6 },
];

export const ROLE_KEYS = ['commercial', 'trader', 'technical'] as const;
export type RoleKey = (typeof ROLE_KEYS)[number];

export const ROLES: Record<RoleKey, { title: string; person: string; scope: string; signed: boolean; at?: string }> = {
  commercial: { get title() { return i18n.t('ak_sd_commercial_dispatcher'); }, get person() { return i18n.t('ak_sd_vadym'); }, get scope() { return i18n.t('ak_sd_forecast_quality_and_volumes'); }, signed: true, get at() { return i18n.t('ak_sd_today_09_12'); } },
  trader: { get title() { return i18n.t('ak_sd_trader'); }, get person() { return i18n.t('ak_sd_olya'); }, get scope() { return i18n.t('ak_sd_prices_and_charge_discharge_windows'); }, signed: true, get at() { return i18n.t('ak_sd_today_09_31'); } },
  technical: { get title() { return i18n.t('ak_sd_technical_dispatcher'); }, get person() { return i18n.t('ak_sd_vacancy'); }, get scope() { return i18n.t('ak_sd_technical_state_and_restrictions'); }, signed: false },
};
